import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import { useSelector } from 'src/utils/store'
import { useDispatch } from 'react-redux'
import { actions } from '../../RestockingState'
import { defaultFulfillmentCenterTypeFilters } from 'src/features/fulfillmentCenterFilters'
import { selectFulfillmentCenters } from 'src/features/selector'
import { SearchRestockListsPayload } from '../../RestockingTypes'
import { capitalizeArray, capitalizeString } from 'src/utils/capitalize'
import dayjs from 'dayjs'
import MultipleSelect from 'src/components/MultipleSelect/MultipleSelect'
import * as S from './FormSearchRestockLists.styles'
import { actions as appActions } from '../../../state'

const FormSearchRestockLists = () => {
  const fulfillmentCenters = useSelector(selectFulfillmentCenters)
  const { filtersSearchLists, selectedFiltersHistory } = useSelector((state) => state.restocking)
  const dispatch = useDispatch()

  const {
    levels, priorities, sourceTypes, status, types,
  } = filtersSearchLists

  const {
    register, handleSubmit, getValues, formState: { errors },
  } = useForm<SearchRestockListsPayload>({ defaultValues: selectedFiltersHistory })

  const onSubmit = (formData: SearchRestockListsPayload) => {
    dispatch(actions.searchRestockingLists(formData))
  }

  useEffect(() => {
    dispatch(appActions.getAvailableFulfillmentCenters(defaultFulfillmentCenterTypeFilters))
  }, [dispatch])

  const fulfillmentCenterOptions = useMemo(() => fulfillmentCenters.map((fulfillmentCenter) => ({
    id: fulfillmentCenter.id,
    description: fulfillmentCenter.name,
  })), [fulfillmentCenters])

  const levelOptions = useMemo(() => capitalizeArray(levels).map((level, index) => ({
    id: index,
    description: level,
  })), [levels])

  const priorityOptions = useMemo(() => capitalizeArray(priorities).map((priority, index) => ({
    id: index,
    description: priority,
  })), [priorities])

  const sourceTypeOptions = useMemo(() => capitalizeArray(sourceTypes).map((sourceType, index) => ({
    id: index,
    description: sourceType,
  })), [sourceTypes])

  const statusOptions = useMemo(() => status.map((option) => ({
    id: option.id,
    description: capitalizeString(option.description),
  })), [status])

  const typesOptions = useMemo(() => types.map((option) => ({
    id: option.id,
    description: capitalizeString(option.description),
  })), [types])

  return (
    <Box
      component='form'
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        gap: '.6rem .5rem',
        width: '100%',
        margin: '1.3rem 0 1.3rem 0',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '.6rem 1rem',
        }}
      >
        <S.CustomSelect
          name='idFulfillmentCenter'
          inputProps={{
            ...register(
              'idFulfillmentCenter',
              { required: 'Campo obrigatório' },
            ),
            'data-testid': 'fc-search-restock-lists',
          }}
          autoComplete='off'
          id='idFulfillmentCenter'
          label='Selecione um FC'
          variant='outlined'
          options={fulfillmentCenterOptions}
          error={!!errors.idFulfillmentCenter}
          helperText={errors.idFulfillmentCenter?.message}
          optionNone={false}
          type='text'
          fullWidth={false}
          autoFocus
          maxHeight='600px'
          defaultValue={getValues('idFulfillmentCenter')}
        />
        <S.CustomTextField
          name='createdDate'
          id='createdDate'
          label='Data de criação'
          type='date'
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...register('createdDate', {
              required: 'Campo obrigatório',
            }),
            'data-testid': 'createdDate-test',
            max: dayjs().format('YYYY-MM-DD'),
          }}
          error={!!errors.createdDate}
          helperText={errors.createdDate?.message}
        />
        <MultipleSelect
          register={register}
          inputName='idType'
          label='Tipo'
          errors={errors.idType}
          options={typesOptions}
          dataTestId='idType-test-id'
          maxHeight='600px'
          width='18rem'
          optionProperty='id'
          defaultOptions={getValues('idType')}
          searchBar
          selectAll
        />
        <MultipleSelect
          register={register}
          inputName='sourceType'
          label='Subtipo'
          errors={errors.sourceType}
          options={sourceTypeOptions}
          dataTestId='sourceType-test-id'
          maxHeight='600px'
          width='13.6rem'
          defaultOptions={getValues('sourceType')}
        />
        <MultipleSelect
          register={register}
          inputName='priority'
          label='Prioridade'
          errors={errors.priority}
          options={priorityOptions}
          dataTestId='priority-test-id'
          searchBar
          maxHeight='600px'
          width='13.6rem'
          selectAll
          defaultOptions={getValues('priority')}
        />
        <MultipleSelect
          register={register}
          inputName='level'
          label='Nível'
          errors={errors.level}
          options={levelOptions}
          dataTestId='level-test-id'
          maxHeight='600px'
          width='13.6rem'
          defaultOptions={getValues('level')}
        />
        <MultipleSelect
          register={register}
          inputName='idStatus'
          label='Status'
          errors={errors.idStatus}
          options={statusOptions}
          dataTestId='idStatus-test-id'
          maxHeight='600px'
          width='13.6rem'
          optionProperty='id'
          defaultOptions={getValues('idStatus')}
        />
      </Box>
      <S.CustomButton
        title='Buscar'
        type='submit'
        bgColor='blue'
        hoverColor='darkBlueHover'
        letterColor='white'
        data-testid='search-lists-button'
      />
    </Box>
  )
}

export default FormSearchRestockLists
